<template>
    <v-container class="fill-height" fluid>
        <v-row align="center" justify="center">
            <v-col cols="12" sm="8" md="5">
                <v-card class="elevation-12">
                    <v-toolbar color="primary">
                        <v-toolbar-title>{{ title }}</v-toolbar-title>
                    </v-toolbar>

                    <!-- Loading -->
                    <v-card-text v-if="loading">
                        <div class="text-center pa-4">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        </div>
                    </v-card-text>

                    <!-- Step 1: NIC setup -->
                    <v-card-text v-else-if="nicSetupMode">
                        <NicSetup @done="onNicDone" />
                    </v-card-text>

                    <!-- Step 2: Discord OAuth setup -->
                    <v-card-text v-else-if="setupMode">
                        <div class="text-body-1 mb-4">
                            Configure Discord OAuth to continue.
                        </div>
                        <v-form @submit.prevent="submitSetup">
                            <v-text-field v-model="form.client_id" label="Client ID" required></v-text-field>
                            <v-text-field v-model="form.client_secret" label="Client Secret" type="password" required></v-text-field>
                            <v-text-field v-model="form.redirect_url" label="Redirect URL (Optional)" placeholder="http://host:8030/auth/discord/callback"></v-text-field>
                            <v-btn type="submit" block color="primary" :loading="submitting" class="mt-4">Save &amp; Continue</v-btn>
                        </v-form>
                    </v-card-text>

                    <!-- Step 3: Login -->
                    <v-card-text v-else>
                        <div class="text-center pa-4">
                            Login with your Discord account to continue.
                        </div>
                        <div class="text-center pb-4">
                            <v-btn color="#5865F2" size="x-large" href="/auth/discord/login" prepend-icon="mdi-discord">
                                Login with Discord
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import NicSetup from '@/components/NicSetup.vue';

export default defineComponent({
    name: 'Login',
    components: { NicSetup },
    setup() {
        const loading = ref(true);
        const nicSetupMode = ref(false);
        const setupMode = ref(false);
        const submitting = ref(false);

        const form = ref({
            client_id: '',
            client_secret: '',
            redirect_url: '',
        });

        const title = computed(() => {
            if (nicSetupMode.value) return 'Network Setup';
            if (setupMode.value) return 'Initial Setup';
            return 'Login';
        });

        const checkConfig = async () => {
            try {
                const [nicRes, authRes] = await Promise.all([
                    fetch('/api/nic/config'),
                    fetch('/api/auth/client'),
                ]);

                if (nicRes.ok) {
                    const nicData = await nicRes.json();
                    if (!nicData.is_configured) {
                        nicSetupMode.value = true;
                        return;
                    }
                }

                if (authRes.ok) {
                    const authData = await authRes.json();
                    setupMode.value = !authData.configured;
                }
            } catch (e) {
                console.error(e);
            } finally {
                loading.value = false;
            }
        };

        const onNicDone = async () => {
            nicSetupMode.value = false;
            try {
                const res = await fetch('/api/auth/client');
                if (res.ok) {
                    const data = await res.json();
                    setupMode.value = !data.configured;
                }
            } catch (e) {
                console.error(e);
            }
        };

        const submitSetup = async () => {
            submitting.value = true;
            try {
                const res = await fetch('/api/auth/setup', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form.value),
                });
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(txt);
                }
                alert('Configuration saved!');
                setupMode.value = false;
            } catch (e: any) {
                alert('Setup failed: ' + e.message);
            } finally {
                submitting.value = false;
            }
        };

        onMounted(checkConfig);

        return { loading, nicSetupMode, setupMode, form, submitting, title, submitSetup, onNicDone };
    },
});
</script>
